/**
 * Light / dark switch. The Flutter app carries both token sets (`RestoColors.light` /
 * `darkTokens`); on the web they are CSS variables toggled by the `dark` class on <html>.
 */
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type Mode = 'light' | 'dark';

const THEME_KEY = 'resto.theme';

interface ThemeValue {
    mode: Mode;
    isDark: boolean;
    toggle: () => void;
    setMode: (mode: Mode) => void;
}

const ThemeContext = createContext<ThemeValue | undefined>(undefined);

export const useTheme = (): ThemeValue => {
    const value = useContext(ThemeContext);
    if (!value) throw new Error('useTheme must be used within a ThemeProvider');
    return value;
};

const initialMode = (): Mode => {
    try {
        const saved = localStorage.getItem(THEME_KEY);
        if (saved === 'dark' || saved === 'light') return saved;
    } catch {
        /* private mode */
    }
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [mode, setModeState] = useState<Mode>(initialMode);

    useEffect(() => {
        document.documentElement.classList.toggle('dark', mode === 'dark');
        try {
            localStorage.setItem(THEME_KEY, mode);
        } catch {
            /* private mode */
        }
    }, [mode]);

    const setMode = useCallback((next: Mode) => setModeState(next), []);
    const toggle = useCallback(() => setModeState((m) => (m === 'dark' ? 'light' : 'dark')), []);

    const value = useMemo(() => ({ mode, isDark: mode === 'dark', toggle, setMode }), [mode, toggle, setMode]);
    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
